import * as XLSX from "xlsx";

import { supabase } from "~/integrations/supabase/client";
import type { Database } from "~/types/supabase";

/** One consolidated row of the "Variación Real vs Teórico" comparison (RPC output). */
export type InventoryVarianceRow =
  Database["public"]["Functions"]["get_inventory_variance"]["Returns"][number];

/** One row of the Softland month-end file, mapped for upload into inventory_physical_counts. */
export interface InventoryCountImportRow {
  code: string;
  name?: string | null;
  real_qty: number;
  unit_cost: number | null;
  total_cost?: number | null;
  /** Raw Planta token from the file (e.g. "1", "2", "NC"); the RPC normalizes it to P1/P2/NC/CONS. */
  plant?: string | null;
}

export interface InventoryCountImportResult {
  imported: number;
  unmatched: string[];
}

/** One per-plant row of the physical count (excludes the derived CONS aggregate). */
export interface InventoryCountByPlantRow {
  match_type: string;
  bucket: string;
  plant: string;
  code: string;
  name: string | null;
  unit: string | null;
  real_qty: number;
  unit_cost: number | null;
  total_cost: number | null;
}

// import_inventory_count was added after the last supabase types regeneration; call it through a
// thin typed wrapper (same approach as costingService.rpcCall / facturaCostsService).
const rpcCall = supabase.rpc.bind(supabase) as unknown as <T>(
  fn: string,
  args: Record<string, unknown>
) => Promise<{ data: T | null; error: { message: string } | null }>;

export const inventoryVarianceService = {
  /** Read-only consolidated comparison: real (Softland) vs system stock, valued. */
  async getVariance(countDate = "2026-06-30"): Promise<InventoryVarianceRow[]> {
    const { data, error } = await supabase.rpc("get_inventory_variance", {
      p_count_date: countDate,
    });
    if (error) throw error;
    return (data ?? []) as InventoryVarianceRow[];
  },

  /**
   * Upsert a month-end physical count (Softland "Informe de Stock Valorizado" file) into
   * `inventory_physical_counts` for the given month-end date. Each row may carry a per-row `plant`
   * token (P1/P2/NC); the RPC normalizes it and derives the CONS aggregate per code, so the Variación
   * report and the costing month-end anchor (both read plant='CONS') are unaffected. `plant` is the
   * fallback applied to rows without a token (defaults to CONS). Returns how many rows were imported
   * and which codes matched no catalog item (`unmatched`).
   */
  async importCount(input: {
    countDate: string;
    plant?: string;
    rows: InventoryCountImportRow[];
  }): Promise<InventoryCountImportResult> {
    const { data, error } = await rpcCall<InventoryCountImportResult>(
      "import_inventory_count",
      {
        p_count_date: input.countDate,
        p_plant: input.plant ?? "CONS",
        p_rows: input.rows,
      }
    );
    if (error) throw new Error(error.message);
    return data ?? { imported: 0, unmatched: [] };
  },

  /** Read-only per-plant breakdown of the month-end count (P1/P2/NC rows, excludes derived CONS). */
  async getCountByPlant(countDate: string): Promise<InventoryCountByPlantRow[]> {
    const { data, error } = await rpcCall<InventoryCountByPlantRow[]>(
      "get_inventory_count_by_plant",
      { p_count_date: countDate }
    );
    if (error) throw new Error(error.message);
    return data ?? [];
  },
};

const round = (n: number | null, d = 2) =>
  n == null ? null : Math.round(n * 10 ** d) / 10 ** d;

const typeLabel = (t: string) =>
  t === "oil"
    ? "Aceite"
    : t === "raw_material"
      ? "Materia Prima"
      : t === "product"
        ? "Producto"
        : "Sin registro";

/** Export the consolidated variance to an .xlsx workbook. */
export function exportInventoryVarianceToExcel(
  rows: InventoryVarianceRow[],
  countDate: string
) {
  const sorted = [...rows].sort(
    (a, b) =>
      (a.bucket ?? "").localeCompare(b.bucket ?? "") ||
      (a.grupo ?? "").localeCompare(b.grupo ?? "") ||
      a.name.localeCompare(b.name)
  );

  const sheet = sorted.map((r) => ({
    Bucket: r.bucket,
    Grupo: r.grupo,
    "Sub Grupo": r.subgrupo,
    Código: r.code,
    Nombre: r.name,
    Tipo: typeLabel(r.match_type),
    Unidad: r.base_unit,
    "Cantidad real": round(r.real_qty),
    "Costo unit. real (CLP)": round(r.real_unit_cost, 0),
    "Valorizado real (CLP)": round(r.real_valorizado, 0),
    "Cantidad sistema": r.in_system ? round(r.teorico_qty) : null,
    "Costo unit. sistema (CLP)": r.in_system ? round(r.teorico_unit_cost, 0) : null,
    "Valorizado sistema (CLP)": r.in_system ? round(r.teorico_valorizado, 0) : null,
    "Dif. cantidad": r.in_system
      ? round((r.real_qty ?? 0) - (r.teorico_qty ?? 0))
      : null,
    "Dif. costo (CLP)": r.in_system ? round(r.diferencia_valorizada, 0) : null,
    "En sistema": r.in_system ? "Sí" : "No",
  }));

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(sheet);
  ws["!cols"] = [
    { wch: 6 }, { wch: 24 }, { wch: 20 }, { wch: 14 }, { wch: 44 },
    { wch: 14 }, { wch: 8 }, { wch: 14 }, { wch: 18 }, { wch: 20 },
    { wch: 16 }, { wch: 18 }, { wch: 20 }, { wch: 16 }, { wch: 18 }, { wch: 10 },
  ];
  XLSX.utils.book_append_sheet(wb, ws, "Variación Consolidada");
  XLSX.writeFile(wb, `Variacion_Real_vs_Teorico_${countDate}.xlsx`);
}
